import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const DEFAULT = {
  app_name: "My NutriTrack",
  logo_line1: "MY",
  logo_line2: "NUTRITRACK",
  logo_image: null,
  primary_color: "#0E9F6E",
  secondary_color: "#E8F5F1",
  heading_font: "Manrope",
  body_font: "IBM Plex Sans",
  login_title: "Welcome",
  login_subtitle: "Sign in to your nutrition account.",
  hero_title: "Personalised nutrition, one meal at a time.",
  hero_subtitle: "Track meals, follow your plan, and stay aligned with your dietitian.",
  footer_text: "All rights reserved · Dt. Dana Shakaa",
  privacy_url: "/privacy",
  contact_url: "/contact",
};

function hexToHsl(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hh = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hh = (g - b) / d + (g < b ? 6 : 0); break;
      case g: hh = (b - r) / d + 2; break;
      case b: hh = (r - g) / d + 4; break;
      default: break;
    }
    hh *= 60;
  }
  return `${Math.round(hh)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

const FONT_FAMILIES = [
  "Manrope", "Inter", "Poppins", "Roboto", "IBM Plex Sans",
  "Space Grotesk", "Plus Jakarta Sans", "Nunito", "DM Sans", "Work Sans",
  "Lato", "Open Sans", "Montserrat", "Merriweather", "Playfair Display",
];

function loadGoogleFont(family) {
  if (!family) return;
  const id = `gf-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:wght@400;500;600;700;800&display=swap`;
  document.head.appendChild(link);
}

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);

  const apply = useCallback((s) => {
    const root = document.documentElement;
    try {
      const primaryHsl = hexToHsl(s.primary_color || DEFAULT.primary_color);
      const secondaryHsl = hexToHsl(s.secondary_color || DEFAULT.secondary_color);
      root.style.setProperty("--primary", primaryHsl);
      root.style.setProperty("--ring", primaryHsl);
      root.style.setProperty("--secondary", secondaryHsl);
      root.style.setProperty("--accent", secondaryHsl);
    } catch (e) { /* invalid hex */ }
    loadGoogleFont(s.heading_font);
    loadGoogleFont(s.body_font);
    document.body.style.fontFamily = `'${s.body_font || "IBM Plex Sans"}', system-ui, sans-serif`;
    document.documentElement.style.setProperty("--font-heading", `'${s.heading_font || "Manrope"}', system-ui, sans-serif`);
    document.title = s.app_name || "My NutriTrack";
  }, []);

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      const merged = { ...DEFAULT, ...data };
      setSettings(merged);
      apply(merged);
    } catch (e) {
      apply(DEFAULT);
    } finally {
      setLoading(false);
    }
  }, [apply]);

  useEffect(() => { reload(); }, [reload]);

  return (
    <SettingsContext.Provider value={{ settings, loading, reload, apply, FONT_FAMILIES }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be inside SettingsProvider");
  return ctx;
}
